/**
 * Secure Key Storage for E2E Encryption Keys
 *
 * Encrypts private keys at rest in localStorage using a user-derived key
 * This prevents XSS attacks from directly stealing private keys
 *
 * Security Features:
 * - PBKDF2 key derivation from user passphrase
 * - Encrypted storage in localStorage
 * - Salt per user for key derivation
 * - Authentication tag to detect tampering
 */

import nacl from 'tweetnacl';
import { encodeBase64, decodeBase64 } from 'tweetnacl-util';
import type { KeyPair, SerializedKeyPair } from './e2e';
import { publicKeyToString, publicKeyFromString } from './e2e';

const STORAGE_KEY = 'privacy_forum_e2e_keypair_v2'; // v2 = encrypted storage
const SALT_KEY = 'privacy_forum_salt';
const ITERATIONS = 100000; // PBKDF2 iterations

export interface EncryptedKeyStorage {
  encryptedPrivateKey: string; // base64 encoded encrypted private key
  publicKey: string; // hex encoded public key (not sensitive)
  nonce: string; // base64 encoded nonce for encryption
  version: number; // storage format version
}

/**
 * Derive an encryption key from a user passphrase using PBKDF2
 */
async function deriveKey(passphrase: string, salt: Uint8Array): Promise<Uint8Array> {
  const encoder = new TextEncoder();
  const passphraseKey = await crypto.subtle.importKey(
    'raw',
    encoder.encode(passphrase),
    'PBKDF2',
    false,
    ['deriveBits']
  );

  const derivedBits = await crypto.subtle.deriveBits(
    {
      name: 'PBKDF2',
      salt,
      iterations: ITERATIONS,
      hash: 'SHA-256',
    },
    passphraseKey,
    256 // 32 bytes
  );

  return new Uint8Array(derivedBits);
}

/**
 * Get or generate salt for key derivation
 */
function getSalt(): Uint8Array {
  const storedSalt = localStorage.getItem(SALT_KEY);

  if (storedSalt) {
    return decodeBase64(storedSalt);
  }

  // Generate new salt
  const salt = nacl.randomBytes(32);
  localStorage.setItem(SALT_KEY, encodeBase64(salt));
  return salt;
}

/**
 * Encrypt private key using derived key from passphrase
 */
async function encryptPrivateKey(
  privateKey: Uint8Array,
  passphrase: string
): Promise<{ encrypted: string; nonce: string }> {
  const salt = getSalt();
  const derivedKey = await deriveKey(passphrase, salt);

  // Generate random nonce
  const nonce = nacl.randomBytes(24);

  // Encrypt using secretbox (symmetric encryption)
  const encrypted = nacl.secretbox(privateKey, nonce, derivedKey);

  if (!encrypted) {
    throw new Error('Failed to encrypt private key: nacl.secretbox returned null');
  }

  return {
    encrypted: encodeBase64(encrypted),
    nonce: encodeBase64(nonce),
  };
}

/**
 * Decrypt private key using passphrase
 */
async function decryptPrivateKey(
  encrypted: string,
  nonce: string,
  passphrase: string
): Promise<Uint8Array> {
  const salt = getSalt();
  const derivedKey = await deriveKey(passphrase, salt);

  const encryptedBytes = decodeBase64(encrypted);
  const nonceBytes = decodeBase64(nonce);

  const decrypted = nacl.secretbox.open(encryptedBytes, nonceBytes, derivedKey);

  if (!decrypted) {
    throw new Error('Failed to decrypt private key: invalid passphrase or corrupted data');
  }

  return decrypted;
}

/**
 * Store keypair encrypted with passphrase
 */
export async function storeEncryptedKeyPair(
  keyPair: KeyPair,
  passphrase: string
): Promise<void> {
  const { encrypted, nonce } = await encryptPrivateKey(keyPair.privateKey, passphrase);

  const storage: EncryptedKeyStorage = {
    encryptedPrivateKey: encrypted,
    publicKey: publicKeyToString(keyPair.publicKey),
    nonce,
    version: 1,
  };

  localStorage.setItem(STORAGE_KEY, JSON.stringify(storage));
}

/**
 * Load keypair from encrypted storage
 */
export async function loadEncryptedKeyPair(passphrase: string): Promise<KeyPair | null> {
  const stored = localStorage.getItem(STORAGE_KEY);

  if (!stored) {
    return null;
  }

  try {
    const storage: EncryptedKeyStorage = JSON.parse(stored);

    const privateKey = await decryptPrivateKey(
      storage.encryptedPrivateKey,
      storage.nonce,
      passphrase
    );

    const publicKey = publicKeyFromString(storage.publicKey);

    return {
      publicKey,
      privateKey,
    };
  } catch (error) {
    console.error('Failed to load encrypted keypair:', error);
    throw new Error('Invalid passphrase or corrupted key data');
  }
}

/**
 * Check if encrypted keypair exists in storage
 */
export function hasEncryptedKeyPair(): boolean {
  return localStorage.getItem(STORAGE_KEY) !== null;
}

/**
 * Clear encrypted keypair from storage
 */
export function clearEncryptedKeyPair(): void {
  localStorage.removeItem(STORAGE_KEY);
  // Keep salt for potential future use
}

/**
 * Get public key without requiring passphrase
 */
export function getPublicKey(): string | null {
  const stored = localStorage.getItem(STORAGE_KEY);

  if (!stored) {
    return null;
  }

  try {
    const storage: EncryptedKeyStorage = JSON.parse(stored);
    return storage.publicKey;
  } catch (error) {
    return null;
  }
}

/**
 * Legacy: Store keypair in plaintext (DEPRECATED - use encrypted storage)
 * This is kept for migration purposes only
 */
export function storePlaintextKeyPair(serialized: SerializedKeyPair): void {
  console.warn('DEPRECATED: Storing keys in plaintext. Please use storeEncryptedKeyPair instead.');
  localStorage.setItem(STORAGE_KEY, JSON.stringify(serialized));
}

/**
 * Legacy: Load plaintext keypair (DEPRECATED)
 */
export function loadPlaintextKeyPair(): SerializedKeyPair | null {
  const stored = localStorage.getItem(STORAGE_KEY);

  if (!stored) {
    return null;
  }

  try {
    const data = JSON.parse(stored);

    // Check if it's encrypted storage format
    if (data.version && data.encryptedPrivateKey) {
      throw new Error('Keys are encrypted. Use loadEncryptedKeyPair() instead.');
    }

    return data as SerializedKeyPair;
  } catch (error) {
    return null;
  }
}

/**
 * Migrate from plaintext to encrypted storage
 */
export async function migrateToEncryptedStorage(
  passphrase: string
): Promise<boolean> {
  const plaintext = loadPlaintextKeyPair();

  if (!plaintext) {
    return false;
  }

  try {
    const keyPair: KeyPair = {
      publicKey: publicKeyFromString(plaintext.publicKey),
      privateKey: publicKeyFromString(plaintext.privateKey),
    };

    await storeEncryptedKeyPair(keyPair, passphrase);

    console.log('Successfully migrated keys to encrypted storage');
    return true;
  } catch (error) {
    console.error('Failed to migrate keys:', error);
    return false;
  }
}

/**
 * Validate passphrase by attempting to decrypt
 */
export async function validatePassphrase(passphrase: string): Promise<boolean> {
  try {
    const keyPair = await loadEncryptedKeyPair(passphrase);
    return keyPair !== null;
  } catch (error) {
    return false;
  }
}

/**
 * Change passphrase for encrypted keys
 */
export async function changePassphrase(
  oldPassphrase: string,
  newPassphrase: string
): Promise<void> {
  // Load with old passphrase
  const keyPair = await loadEncryptedKeyPair(oldPassphrase);

  if (!keyPair) {
    throw new Error('Failed to load keys with old passphrase');
  }

  // Store with new passphrase
  await storeEncryptedKeyPair(keyPair, newPassphrase);
}
